// Reachability hook + synchronous accessor (offline-resilience substrate).
//
// Mirrors the canonical online-check baked into src/lib/native/offlineQueue.ts (its internal,
// non-exported `isOnline()`): on native use @capacitor/network (dynamic-imported so the web bundle
// never hard-depends on it), on web fall back to navigator.onLine + window online/offline events.
//
// A module-level cached boolean is kept in sync by the listeners so `getIsOnline()` is synchronous
// (React Query placeholderData, drain triggers, etc. need a sync read). It defaults to ONLINE when
// connectivity is unknown (SSR / no navigator / plugin error) so content is never hidden by mistake
// — fail-open, exactly like the queue's reachability primitive.

import { useEffect, useState } from 'react';
import { isNative } from '@/lib/native/platform';

// Module-level cache, updated by every mounted listener. Seeded fail-open (online).
let onlineCache: boolean =
  typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean'
    ? true
    : navigator.onLine;

// Subscribers (mounted hooks) notified whenever the cached value flips.
const subscribers = new Set<(online: boolean) => void>();

function setOnline(next: boolean): void {
  if (next === onlineCache) return;
  onlineCache = next;
  for (const fn of subscribers) {
    try {
      fn(next);
    } catch {
      /* a subscriber throwing must never break the broadcast */
    }
  }
}

/**
 * Synchronous best-effort reachability read from the module cache. Returns true (online) when
 * connectivity is unknown. Safe to call anywhere (no hook rules), including outside React.
 */
export function getIsOnline(): boolean {
  return onlineCache;
}

/**
 * Reactive online/offline boolean. True === online. Defaults online on first render so cached/offline
 * content is never hidden before connectivity is known. Native uses @capacitor/network; web uses
 * navigator.onLine + window 'online'/'offline'. All native access is dynamic-imported + try/catch so
 * the web build is unaffected.
 */
export function useIsOnline(): boolean {
  const [online, setLocal] = useState<boolean>(() => getIsOnline());

  useEffect(() => {
    let disposed = false;

    // Keep this hook in sync with the shared module cache.
    const sub = (next: boolean) => {
      if (!disposed) setLocal(next);
    };
    subscribers.add(sub);
    // Adopt the current cache immediately (it may have changed between render and effect).
    setLocal(getIsOnline());

    const disposers: Array<() => void> = [];

    if (isNative()) {
      // Native: read once, then subscribe to networkStatusChange. Dynamic import so the web bundle
      // never pulls @capacitor/network in at module-eval time; try/catch so a missing plugin degrades
      // silently (stays fail-open online).
      (async () => {
        try {
          const { Network } = await import('@capacitor/network');
          const status = await Network.getStatus();
          if (!disposed) setOnline(status.connected);
          const handle = await Network.addListener('networkStatusChange', (s) => {
            setOnline(s.connected);
          });
          disposers.push(() => {
            void handle.remove();
          });
        } catch {
          /* plugin unavailable: stay fail-open (online) */
        }
      })();
    } else if (typeof window !== 'undefined') {
      // Web: seed from navigator.onLine and follow window online/offline events.
      if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
        setOnline(navigator.onLine);
      }
      const goOnline = () => setOnline(true);
      const goOffline = () => setOnline(false);
      window.addEventListener('online', goOnline);
      window.addEventListener('offline', goOffline);
      disposers.push(() => {
        window.removeEventListener('online', goOnline);
        window.removeEventListener('offline', goOffline);
      });
    }

    return () => {
      disposed = true;
      subscribers.delete(sub);
      for (const d of disposers) d();
    };
  }, []);

  return online;
}
